export interface MemoValue {
  value: string;
  source?: string;
  history?: Array<{ value: string; source?: string }>;
  stale?: boolean;
}

export type MemoCategoryId =
  | "overview"
  | "status"
  | "parties"
  | "terms"
  | "legal"
  | "governance"
  | "closing";

export interface MemoEntry {
  field: string;
  value: string;
  source?: string;
  stale?: boolean;
  current: boolean;
}

export interface MemoSourceGroup {
  id: string;
  label: string;
  emailNumber: number | null;
  entries: MemoEntry[];
}

export interface MemoChange extends MemoEntry {
  change: "Added" | "Updated";
}

export interface MemoKeyChange {
  field: string;
  category: MemoCategoryId;
  records: MemoEntry[];
}

export const MEMO_CATEGORIES: Array<{ id: MemoCategoryId; label: string }> = [
  { id: "overview", label: "Overview" },
  { id: "status", label: "Current status and outcome" },
  { id: "parties", label: "Parties and people" },
  { id: "terms", label: "Financial or commercial terms" },
  { id: "legal", label: "Legal and diligence subjects" },
  { id: "governance", label: "Governance" },
  { id: "closing", label: "Closing and funds" },
];

function normalized(field: string): string {
  return field.toLowerCase().replace(/[_—–-]+/g, " ").replace(/\s+/g, " ").trim();
}

function includesAny(value: string, terms: string[]): boolean {
  return terms.some((term) => value.includes(term));
}

export function categorizeMemoField(field: string): MemoCategoryId {
  const key = normalized(field);
  if (includesAny(key, ["hourly rate", "billing rate"])) return "terms";
  if (includesAny(key, ["phase", "status", "outcome", "result", "matter state", "current position"])) return "status";
  if (includesAny(key, ["board", "director", "observer", "voting", "vote", "governance", "approval", "consent", "protective provision", "authority", "drag along"])) return "governance";
  if (includesAny(key, ["closing", "signing", "signature", "execution", "executed", "effective date", "funds flow", "funding", "wire", "disbursement", "proceeds", "filing", "filed"])) return "closing";
  if (includesAny(key, ["conflict", "legal", "diligence", "intellectual property", "ip ownership", "governing law", "compliance", "regulatory", "privilege", "confidentiality", "release", "claim", "risk"])) return "legal";
  if (includesAny(key, ["round type", "round size", "valuation", "security", "price", "option pool", "liquidation", "anti dilution", "preference", "pro rata", "economics", "consideration", "escrow", "earnout", "interest", "fee", "amount", "commercial", "exclusivity", "renewal", "spend", "term", "ownership"])) return "terms";
  if (includesAny(key, ["party", "parties", "participant", "client", "counterparty", "issuer", "investor", "counsel", "lawyer", "attorney", "ceo", "cfo", "cmo", "founder", "buyer", "seller", "lender", "borrower", "company", "transfer agent", "advisor", "representative", "signatory", "contact"])) return "parties";
  if (includesAny(key, ["matter name", "matter type", "matter number", "matter code", "deal type", "transaction name", "project name", "engagement name", "overview", "description"])) return "overview";
  return "legal";
}

function partyIdentity(field: string): string {
  return normalized(field)
    .replace(/\bfull legal name\b/g, "")
    .replace(/\bfull name\b/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function preferMoreCompleteParty(
  current: [string, MemoValue] | undefined,
  candidate: [string, MemoValue],
): [string, MemoValue] {
  if (!current) return candidate;
  const currentScore = current[1].value.trim().length + current[0].trim().length;
  const candidateScore = candidate[1].value.trim().length + candidate[0].trim().length;
  return candidateScore > currentScore ? candidate : current;
}

export function groupMemoFields(fields: Record<string, MemoValue>): Record<MemoCategoryId, Array<[string, MemoValue]>> {
  const grouped: Record<MemoCategoryId, Array<[string, MemoValue]>> = {
    overview: [], status: [], parties: [], terms: [], legal: [], governance: [], closing: [],
  };
  const parties = new Map<string, [string, MemoValue]>();
  Object.entries(fields).forEach((entry) => {
    const category = categorizeMemoField(entry[0]);
    if (category !== "parties") grouped[category].push(entry);
    else parties.set(partyIdentity(entry[0]), preferMoreCompleteParty(parties.get(partyIdentity(entry[0])), entry));
  });
  grouped.parties.push(...parties.values());
  return grouped;
}

export function sourceEmailNumber(source?: string): number | null {
  if (!source) return null;
  const match = source.match(/(?:email\s*#?|\bE)(\d+)\b/i);
  return match ? Number(match[1]) : null;
}

export function groupMemoBySource(fields: Record<string, MemoValue>): MemoSourceGroup[] {
  const groups = new Map<string, MemoSourceGroup>();
  const add = (entry: MemoEntry): void => {
    const number = sourceEmailNumber(entry.source);
    const id = number === null ? "other" : `email-${number}`;
    if (!groups.has(id)) groups.set(id, { id, label: number === null ? "Other sources" : `Email #${number}`, emailNumber: number, entries: [] });
    groups.get(id)?.entries.push(entry);
  };
  Object.entries(fields).forEach(([field, memo]) => {
    (memo.history || []).forEach((record) => add({ field, ...record, current: false }));
    add({ field, value: memo.value, source: memo.source, stale: memo.stale, current: true });
  });
  return [...groups.values()].sort((a, b) => {
    if (a.emailNumber === null) return 1;
    if (b.emailNumber === null) return -1;
    return a.emailNumber - b.emailNumber;
  });
}

export function memoChanges(fields: Record<string, MemoValue>): MemoChange[] {
  const changes: MemoChange[] = [];
  Object.entries(fields).forEach(([field, memo]) => {
    const records = [...(memo.history || []), { value: memo.value, source: memo.source }];
    records.forEach((record, index) => changes.push({
      field,
      value: record.value,
      source: record.source,
      stale: index === records.length - 1 ? memo.stale : undefined,
      current: index === records.length - 1,
      change: index === 0 ? "Added" : "Updated",
    }));
  });
  return changes.sort((a, b) => {
    const aEmail = sourceEmailNumber(a.source);
    const bEmail = sourceEmailNumber(b.source);
    if (aEmail === null && bEmail === null) return 0;
    if (aEmail === null) return 1;
    if (bEmail === null) return -1;
    return aEmail - bEmail;
  });
}

/** Material Memo changes only: fields need at least two distinct recorded
 * values. A newly extracted fact is not a negotiation/change history item. */
export function memoKeyChanges(fields: Record<string, MemoValue>): MemoKeyChange[] {
  const changes: MemoKeyChange[] = [];
  Object.entries(fields).forEach(([field, memo]) => {
    const records: MemoEntry[] = [...(memo.history || []), { value: memo.value, source: memo.source }]
      .map((record, index, all) => ({
        field,
        value: record.value,
        source: record.source,
        stale: index === all.length - 1 ? memo.stale : undefined,
        current: index === all.length - 1,
      }))
      .filter((record, index, all) => index === 0 || record.value.trim() !== all[index - 1].value.trim());
    if (records.length < 2) return;
    changes.push({ field, category: categorizeMemoField(field), records });
  });
  const categoryOrder = new Map(MEMO_CATEGORIES.map((category, index) => [category.id, index]));
  return changes.sort((a, b) =>
    (categoryOrder.get(a.category) ?? 99) - (categoryOrder.get(b.category) ?? 99)
      || a.field.localeCompare(b.field),
  );
}
